import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTE_LABEL_EVENT, routeLabel } from '../utils/routeLabels';

/**
 * Custom hook to automatically update browser tab title based on current route
 * 
 * Usage: Call usePageTitle() at the top level of App.jsx or in AdminShell
 * 
 * Route titles are automatically mapped from the URL path
 */
export const usePageTitle = () => {
  const location = useLocation();
  // Re-title when a page registers its real name (utils/routeLabels).
  const [labelVersion, setLabelVersion] = useState(0);
  useEffect(() => {
    const bump = () => setLabelVersion((v) => v + 1);
    window.addEventListener(ROUTE_LABEL_EVENT, bump);
    return () => window.removeEventListener(ROUTE_LABEL_EVENT, bump);
  }, []);

  useEffect(() => {
    // Map routes to user-friendly page titles
    const routeTitleMap = {
      '/payment/success': 'Payment Confirmation',
      '/payment/cancel': 'Checkout Closed',
      // Dashboard
      '/admin': 'Dashboard',
      '/admin/': 'Dashboard',
      
      // Programs
      '/admin/programs': 'Programs',
      '/admin/programs/pca': 'Personal Care Program',
      '/admin/programs/adult-day-services': 'Adult Day Services',
      '/admin/programs/behavioral-health': 'Behavioral Health',
      
      // Scheduling & Calendar
      '/admin/scheduler': 'Scheduler',
      '/admin/calendar': 'Visit Calendar',
      '/admin/alerts': 'Alerts',
      
      // Visits
      '/admin/visit-logs': 'Visit Logs',
      '/admin/visits-today': 'Visits Today',
      '/admin/visit-history': 'Visit History',
      '/admin/visit-management': 'Visit Management',
      '/admin/visit-management/service-codes': 'Service Codes',
      '/admin/visit-management/visit-notes': 'Visit Notes',
      '/admin/visit-management/quality-reviews': 'Quality Reviews',
      '/admin/visit-management/missed-visits': 'Missed Visits',
      
      // Staff/Caregivers
      '/admin/caregivers': 'Providers',
      '/admin/training': 'Training Videos',
      
      // Patients
      '/admin/patients': 'Patients',
      
      // Contacts
      '/admin/contacts': 'Contacts',
      
      // Documents
      '/admin/documents': 'Document Management',
      
      // Clinical
      '/admin/clinical/notes': 'Clinical Notes',
      '/admin/clinical/assessments': 'Clinical Assessments',
      '/admin/clinical/treatment-plans': 'ARMHS Treatment Plans',
      '/admin/clinical/reports': 'Clinical Reports',
      
      // Billing
      '/admin/billing': 'Billing',
      '/admin/billing/invoice-generator': 'Invoice Generator',
      '/admin/billing/batches': 'Billing Batches',
      '/admin/billing/service-codes': 'Service Codes',
      '/admin/billing/payers': 'Payer Management',
      '/admin/billing/office-ally': 'Office Ally Integration',
      '/admin/billing/reports': 'Billing Reports',
      '/admin/billing/cms1500-demo': 'CMS-1500 Demo',
      '/admin/billing/cms1500-management': 'CMS-1500 Management',
      
      // Compliance
      '/admin/compliance': 'Compliance Issues',
      '/admin/compliance/audit-logs': 'Audit Logs',
      '/admin/compliance/data-readiness': 'Data Readiness',
      
      // Communications
      '/admin/communication': 'Communication',
      
      // Settings & Configuration
      '/admin/settings': 'Settings',
      '/admin/settings/timezone': 'Timezone Settings',
      '/admin/offices': 'Office Locations',
      
      // Reports & Analytics
      '/admin/reports': 'Reports',
      '/admin/reports/245d': '245D reports',
      '/admin/analytics': 'Analytics',
      '/admin/service-lines': 'Service Lines',
      
      // System Administration
      '/admin/users': 'System Users',
      '/admin/provider-types': 'Provider Type Management',
      '/admin/permissions': 'Permissions Management',
      '/admin/system': 'System Health',
      
      // HHA Integration
      '/admin/hha-status': 'HHA Status Dashboard',
      '/admin/eligibility': 'Eligibility Check',
      
      // Platform Admin
      '/platform': 'Platform Dashboard',
      '/platform/organizations': 'Organizations',
      
      // Login
      '/login': 'Login',
    };

    // Get the current path
    let currentPath = location.pathname;
    
    // Check for exact match first
    let pageTitle = routeTitleMap[currentPath];
    
    // If no exact match, check for dynamic routes
    if (!pageTitle) {
      // Handle dynamic visit detail pages
      if (currentPath.match(/^\/admin\/visits\/[^/]+$/)) {
        pageTitle = 'Visit Details';
      }
      // Handle dynamic program pages
      else if (currentPath.match(/^\/admin\/programs\/[^/]+$/)) {
        const programSlug = currentPath.split('/').pop();
        const isOpaqueProgramId = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(programSlug);
        pageTitle = routeLabel(programSlug) || (isOpaqueProgramId
          ? 'Program'
          : programSlug.split('-').map(word =>
            // License chapters keep their capital letter: 245d reads 245D, 144g 144G.
            /^\d+[a-z]$/i.test(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' '));
      }
      // Handle document viewers
      else if (currentPath.match(/^\/progress-notes\//)) {
        pageTitle = 'Progress Note';
      }
      else if (currentPath.match(/^\/health-reviews\//)) {
        pageTitle = 'Health Review';
      }
      else if (currentPath.match(/^\/diagnostic-assessments\//)) {
        pageTitle = 'Diagnostic Assessment';
      }
    }
    
    // Update document title
    if (pageTitle) {
      document.title = `${pageTitle} - AraSync`;
    } else {
      // Default fallback
      document.title = 'AraSync';
    }
  }, [location, labelVersion]);
};

export default usePageTitle;
